import React from 'react';

const DEFAULT_BUTTON_VARIANT = 'primary';
const BACK_BUTTON_VARIANT = 'outline';
const BUTTON_ICON_START_POSITION = 'start';
const BUTTON_SLOT_CLASS = 'w-full sm:w-44';
const BUTTON_CLASS = 'inline-flex w-full items-center justify-center';

export interface BottomNavigationAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
  disabled?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  tooltip?: string;
}

interface BottomNavigationBarProps {
  backAction?: BottomNavigationAction;
  primaryActions?: BottomNavigationAction[];
  className?: string;
  containerClassName?: string;
}

/**
 * BottomNavigationBar - Consistent bottom navigation component for all pages
 * Provides standardized back navigation and action buttons across the application
 */
export const BottomNavigationBar: React.FC<BottomNavigationBarProps> = ({
  backAction,
  primaryActions = [],
  className = '',
  containerClassName = '',
}) => {
  const getButtonClasses = (
    variant: BottomNavigationAction['variant'] = DEFAULT_BUTTON_VARIANT,
    disabled = false,
  ) => {
    const baseClasses =
      'px-6 py-2 rounded-lg transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-offset-2';

    if (disabled) {
      return `${baseClasses} bg-gray-300 text-gray-500 cursor-not-allowed`;
    }

    switch (variant) {
      case 'primary':
        return [
          baseClasses,
          'bg-[#336094] text-white hover:bg-[#2a4d7a]',
          'focus:ring-[#336094] main-button',
        ].join(' ');
      case 'secondary':
        return [
          baseClasses,
          'text-[#336094] border border-[#336094]',
          'hover:bg-[#336094] hover:text-white focus:ring-[#336094]',
        ].join(' ');
      case 'outline':
        return `${baseClasses} text-gray-600 border border-gray-300 hover:bg-gray-50 focus:ring-gray-500`;
      default:
        return [
          baseClasses,
          'bg-[#336094] text-white hover:bg-[#2a4d7a]',
          'focus:ring-[#336094] main-button',
        ].join(' ');
    }
  };

  const renderButtonContent = (action: BottomNavigationAction) => {
    const iconPosition = action.iconPosition ?? BUTTON_ICON_START_POSITION;

    return (
      <div className="flex items-center justify-center gap-2">
        {iconPosition === 'end' ? (
          <>
            <span>{action.label}</span>
            {action.icon}
          </>
        ) : (
          <>
            {action.icon}
            <span>{action.label}</span>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={`mt-8 ${containerClassName}`}>
      <div
        className={[
          'flex flex-col gap-3 bg-white border border-gray-200',
          'sm:flex-row sm:items-center sm:justify-between',
          'rounded-lg p-6',
          className,
        ].join(' ')}
      >
        <div className={BUTTON_SLOT_CLASS}>
          {backAction && (
            <div className={`relative group ${BUTTON_SLOT_CLASS}`}>
              <button
                onClick={backAction.onClick}
                disabled={backAction.disabled}
                className={[
                  getButtonClasses(
                    BACK_BUTTON_VARIANT,
                    backAction.disabled,
                  ),
                  BUTTON_CLASS,
                ].join(' ')}
              >
                {renderButtonContent(backAction)}
              </button>
              {backAction.tooltip && backAction.disabled && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  {backAction.tooltip}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Primary Actions */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          {primaryActions.map((action, index) => (
            <div key={index} className={`relative group ${BUTTON_SLOT_CLASS}`}>
              <button
                onClick={action.onClick}
                disabled={action.disabled}
                className={[
                  getButtonClasses(action.variant, action.disabled),
                  BUTTON_CLASS,
                ].join(' ')}
              >
                {renderButtonContent(action)}
              </button>
              {action.tooltip && action.disabled && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  {action.tooltip}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
